import { Tabs, TabsContent, TabsList, TabsTrigger } from "@primitives/tabs";
import { useNavigationState } from "@/zustand/NavigationState";

import FileAttachmentList from "./files/FileAttachmentList";
import LinkList from "./links/LinkList";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";
import ChatMembersList from "./members/ChatMembersList";
import MediaAttachmentList from "./media/MediaAttachmentList";
import RecordingsList from "./recordings/RecordingsList";
import { useTranslation } from "react-i18next";
import { featureFlags } from "@/config/featureFlags";

enum TABS {
  MEDIA = "media",
  FILES = "files",
  LINKS = "links",
  MEMBERS = "members",
  RECORDINGS = "recordings",
  // VOICE = "voice",
}

export default function ChatDetailsTabs() {
  const { t } = useTranslation();
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );

  // Recordings are moderator-only (owner/admin/moderator) and GROUP-only — the
  // backend 403s everyone else, so we never surface a tab that would 403. Reuses
  // the same role source as ChatDetailsActions (no new membership check).
  const isModerator =
    activeChatRoleAsMember === MemberRole.OWNER ||
    activeChatRoleAsMember === MemberRole.ADMIN ||
    activeChatRoleAsMember === MemberRole.MODERATOR;
  // Recordings are part of the Orion Meet UI work — hidden entirely (even for GROUP moderators)
  // when the master flag is OFF, so prod (beta) reproduces the v1.1.27 tab set (no Recordings tab).
  const showRecordings =
    featureFlags.orionMeetUi &&
    activeChatType === ChatType.GROUP &&
    isModerator;

  if (!activeChatId) {
    return null;
  }

  return (
    <Tabs
      defaultValue={TABS.MEDIA}
      className="relative mt-3 flex size-full flex-col overflow-hidden p-3"
    >
      {/* TABS MENU */}
      <TabsList className="flex justify-around">
        <TabsTrigger value={TABS.MEDIA}>
          {t("chatDetailsTabs.media")}
        </TabsTrigger>
        <TabsTrigger value={TABS.FILES}>
          {t("chatDetailsTabs.files")}
        </TabsTrigger>
        <TabsTrigger value={TABS.LINKS}>
          {t("chatDetailsTabs.links")}
        </TabsTrigger>
        {/* <TabsTrigger value={TABS.VOICE}>

          Voice
        </TabsTrigger> */}
        {activeChatType === ChatType.GROUP && (
          <TabsTrigger value={TABS.MEMBERS}>
            {t("chatDetailsTabs.members")}
          </TabsTrigger>
        )}
        {showRecordings && (
          <TabsTrigger value={TABS.RECORDINGS}>
            {t("chatDetailsTabs.recordings")}
          </TabsTrigger>
        )}
      </TabsList>

      {/* TABS CONTENT */}

      {/* Members list */}
      {activeChatType === ChatType.GROUP && (
        <TabsContent
          value={TABS.MEMBERS}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <ChatMembersList />
        </TabsContent>
      )}
      {/* Media attachments list */}
      <TabsContent value={TABS.MEDIA} className="overflow-hidden">
        <MediaAttachmentList />
      </TabsContent>
      {/* File Attachments list */}
      <TabsContent value={TABS.FILES} className="overflow-hidden">
        <FileAttachmentList />
      </TabsContent>
      {/* Links list */}
      <TabsContent value={TABS.LINKS} className="overflow-hidden">
        <LinkList />
      </TabsContent>

      {/* Recordings list (moderator-only, GROUP-only) */}
      {showRecordings && (
        <TabsContent
          value={TABS.RECORDINGS}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <RecordingsList />
        </TabsContent>
      )}

      {/* Voice messages list */}
      {/* <TabsContent value={TABS.VOICE} className="overflow-hidden">
        TEMP
      </TabsContent> */}
    </Tabs>
  );
}
